import {
  Body,
  Box,
  Center,
  Flex,
  Icon,
  Image,
  VStack,
  motion,
} from '@hover/blueprint';
import type { BoxProps } from '@hover/blueprint';
import { iBookmark, iCheck, iHome, iPlus } from '@hover/icons';
import React, { useEffect, useMemo, useState } from 'react';

import {
  formatSpaceType,
  getMostRecentAddable,
} from 'src/modules/homeownerApp/components/Utils';
import { useSaveToProject } from 'src/modules/homeownerApp/hooks';
import { useSaveToProjectContext } from 'src/modules/homeownerApp/providers/SaveToProjectProvider';
import type { Project } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { inferProjectAreasFromSpaceTypes } from '../../Project';

import { ProjectItemContainer } from './ProjectLoadingItem';

const MotionImage = motion(Image);

const FallbackIcon = ({ isSaved = false }: { isSaved: boolean }) => (
  <Center
    backgroundColor="neutral.100"
    borderRadius={300}
    height="80px"
    width="80px"
  >
    <Icon
      fill={isSaved ? 'inherit' : 'none'}
      icon={isSaved ? iBookmark : iHome}
      size="medium"
    />
  </Center>
);

const getProjectAttributes = (project?: Project) => {
  if (!project) {
    return { name: '', imageUrl: '' };
  }
  const { name, addables } = project;
  const uniqueSpaceTypes = inferProjectAreasFromSpaceTypes(addables);
  const coverDisplayImage = getMostRecentAddable(addables);

  return {
    name,
    imageUrl: coverDisplayImage?.aiGeneratedImage.generatedImagePath,
    spaceTypeText: uniqueSpaceTypes.join(', '),
  };
};

const checkmarkFilledState = {
  color: 'white',
  icon: iCheck,
  containerBackgroundColor: 'neutral.900',
  containerBorderColor: 'neutral.900',
};
const checkmarkEmptyState = {
  color: 'black',
  icon: iPlus,
  containerBackgroundColor: 'transparent',
  containerBorderColor: 'neutral.500',
};

const animationVariants = {
  hide: {
    opacity: 0.1,
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  show: {
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
};

interface ProjectMenuItemProps extends BoxProps {
  project?: Project;
  isChecked?: boolean;
}

export const ProjectMenuItem = ({
  project,
  isChecked = false,
  onClick,
  ...props
}: ProjectMenuItemProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { generatedImageToSave, setImageToSave } = useSaveToProjectContext();
  const { handleAddToProject, handleRemoveFromProject } = useSaveToProject({
    location: 'thumbnail',
  });
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { name, imageUrl, spaceTypeText } = useMemo(
    () => getProjectAttributes(project),
    [project],
  );

  const { color, icon, containerBackgroundColor, containerBorderColor } =
    isChecked ? checkmarkFilledState : checkmarkEmptyState;

  const spaceType = formatSpaceType(spaceTypeText);
  const [show, setShow] = useState(isChecked || !!imageUrl);
  const [hasImage] = useState(!!imageUrl);

  const onAddToProject = async () => {
    setIsLoading(true);
    await handleAddToProject(project, generatedImageToSave);
    setIsLoading(false);
    setImageToSave(undefined);
  };

  const onRemoveFromProject = async () => {
    setIsLoading(true);
    await handleRemoveFromProject(project, generatedImageToSave);
    setIsLoading(false);
    setImageToSave(undefined);
  };

  const handleClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): Promise<any> => {
    setShow(!isChecked);

    if (onClick) {
      onClick(event);
      return Promise.resolve();
    }

    return isChecked ? onRemoveFromProject() : onAddToProject();
  };

  useEffect(() => {
    if (!isLoading) {
      setShow(isChecked || !!imageUrl);
    }
  }, [imageUrl, isChecked, isLoading]);

  return (
    <ProjectItemContainer {...props} onClick={handleClick}>
      <Flex alignItems="center" height="80px">
        <Box height="80px" minHeight="80px" minWidth="80px" width="80px">
          {imageUrl ? (
            <MotionImage
              alt={`project: ${name}`}
              animate={show || hasImage ? 'show' : 'hide'}
              borderRadius={300}
              height="100%"
              initial={show ? 'hide' : 'show'}
              key={imageUrl}
              objectFit="cover"
              objectPosition="center"
              src={imageUrl}
              variants={animationVariants}
              width="100%"
            />
          ) : (
            <FallbackIcon isSaved={!project} />
          )}
        </Box>
        <VStack
          alignItems="flex-start"
          flex={1}
          height="100%"
          paddingLeft={500}
        >
          <Body
            isSemiBold
            margin={0}
            noOfLines={1}
            pt={spaceTypeText ? 5 : '30px'}
            textOverflow="ellipsis"
            transition=".5s"
          >
            {name || t('customize.addToProject.savedIdeas')}
          </Body>
          {spaceTypeText && (
            <motion.div
              animate={show ? 'show' : 'hide'}
              color="neutral.500"
              initial={show ? 'hide' : 'show'}
              key={spaceType}
              variants={animationVariants}
            >
              {spaceType}
            </motion.div>
          )}
        </VStack>
      </Flex>
      <Center
        backgroundColor={isLoading ? 'neutral.500' : containerBackgroundColor}
        borderColor={containerBorderColor}
        borderRadius={300}
        borderStyle="solid"
        borderWidth="1px"
        height="300"
        minWidth="300"
        transition="background-color 0.2s, border-color 0.2s"
        width="300"
      >
        <Icon color={isLoading ? 'neutral.500' : color} icon={icon} />
      </Center>
    </ProjectItemContainer>
  );
};
